import { InfoIcon } from 'lucide-react';
import { CloudIcon } from 'lucide-react';

import { Container } from '@/components/legacy-ui';
import { Tooltip } from '@/components/Tooltip';
import { MonitoringCounts, SubscriptionConfiguration } from '@/types';
import { calculateCloudProviderCount } from '@/utils/monitoring/transformers';

import { CountBadge } from './CountBadge';
import { IncludedBadge } from './IncludedBadge';
import { NotIncludedBadge } from './NotIncludedBadge';

interface CloudCardProps {
  monitoringCounts: MonitoringCounts;
  subscriptionValue?: SubscriptionConfiguration;
  isLoading: boolean;
}

const cloudProviders: Array<{
  key: keyof MonitoringCounts['cloud'];
  label: string;
}> = [
  { key: 'amazon', label: 'AWS' },
  { key: 'azure', label: 'Azure' },
  { key: 'gcp', label: 'GCP' },
  { key: 'digitalocean', label: 'Digital Ocean' },
];

export function CloudCard({
  monitoringCounts,
  subscriptionValue,
  isLoading,
}: CloudCardProps) {
  const attackSurface = subscriptionValue?.attackSurface;

  const getCloudProviderTooltip = (providerLabel: string) => {
    if (attackSurface?.cloud) {
      return `External and internal assets from ${providerLabel} cloud infrastructure`;
    }
    if (attackSurface?.external) {
      return `External assets from ${providerLabel} cloud infrastructure`;
    }
    return '';
  };

  const cloudEnabled = Boolean(attackSurface?.cloud);
  const externalOnly = !cloudEnabled && Boolean(attackSurface?.external);
  const cloudCovered = cloudEnabled || externalOnly;

  const cloudTotal = cloudProviders.reduce(
    (sum, { key }) =>
      sum +
      calculateCloudProviderCount(monitoringCounts.cloud[key], {
        cloud: Boolean(attackSurface?.cloud),
        external: Boolean(attackSurface?.external),
      }),
    0
  );

  return (
    <Container className="flex h-full w-full flex-col gap-3">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-base font-semibold">Cloud</h3>
        <div className="flex items-center gap-2">
          {externalOnly && (
            <span className="text-chariot-status-warning border-chariot-status-warning rounded-full border px-2 py-0.5 text-xs font-semibold uppercase">
              External Only
            </span>
          )}
          {cloudCovered ? <IncludedBadge /> : <NotIncludedBadge />}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <CloudIcon className="text-chariot-stroke size-6 shrink-0 [&_path]:stroke-[2px]" />
        <span className="text-sm font-medium whitespace-break-spaces">
          Cloud Assets Monitored
        </span>
        <CountBadge value={cloudTotal} isLoading={isLoading} />
      </div>
      <div className="bg-chariot-stroke h-px w-full" />
      <div className="flex flex-col gap-4">
        {cloudProviders.map(({ key, label }) => {
          const count = calculateCloudProviderCount(
            monitoringCounts.cloud[key],
            {
              cloud: Boolean(attackSurface?.cloud),
              external: Boolean(attackSurface?.external),
            }
          );
          const tooltip = getCloudProviderTooltip(label);
          return (
            <div className="flex w-full items-center justify-between" key={key}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium whitespace-break-spaces capitalize">
                  {label}
                </span>
                {tooltip && (
                  <Tooltip title={tooltip}>
                    <InfoIcon
                      className="text-chariot-text-primary size-4"
                      aria-hidden="true"
                    />
                  </Tooltip>
                )}
              </div>
              <CountBadge value={count} isLoading={isLoading} />
            </div>
          );
        })}
      </div>
    </Container>
  );
}
